import { useQuery } from '@apollo/client'
import AddIcon from '@mui/icons-material/Add'
import PhotoIcon from '@mui/icons-material/AddPhotoAlternate'
import { Alert, Box, Button, Grid, LinearProgress, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { Title, useGetIdentity, useLocale, useTranslate } from 'react-admin'
import { Link } from 'react-router-dom'
import { DASHBOARD, type DashboardData } from './data'
import { Inventory, Portals, Recent, Todo, Upcoming } from './sections'

/** The days ahead the dashboard lists appointments for. */
const DAYS_AHEAD = 14

/**
 * The start page: the inventory by status, the next appointments, what the
 * boats on offer still lack, how the portals are doing, and what was changed
 * last — each a way into the page behind it.
 */
const Dashboard = () => {
  const translate = useTranslate()
  const locale = useLocale()
  const { identity } = useGetIdentity()
  // Fixed for the page's lifetime: a new range would be a new query each render.
  const range = useMemo(() => {
    const from = new Date()
    from.setHours(0, 0, 0, 0)
    const until = new Date(from.getTime() + (DAYS_AHEAD + 1) * 86_400_000)
    return { from: from.toISOString(), until: until.toISOString() }
  }, [])
  const { data, loading, error } = useQuery<DashboardData>(DASHBOARD, {
    variables: range,
    fetchPolicy: 'cache-and-network',
  })

  const hour = new Date().getHours()
  const greeting = translate(
    hour < 11 ? 'dashboard.morning' : hour < 18 ? 'dashboard.day' : 'dashboard.evening'
  )
  // A display name's first word; an e-mail address is no name to greet.
  const first = String(identity?.fullName ?? '').split(' ')[0]
  const name = first && !first.includes('@') ? `, ${first}` : ''
  const today = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date())

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, minWidth: 0 }}>
      <Title title={translate('dashboard.title')} />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ justifyContent: 'space-between', gap: 2, mb: 2 }}
      >
        <Box>
          <Typography variant='h5' component='h1'>
            {greeting}
            {name}
          </Typography>
          <Typography color='text.secondary'>{today}</Typography>
        </Box>
        <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button variant='contained' startIcon={<AddIcon />} component={Link} to='/boats/create'>
            {translate('dashboard.new_boat')}
          </Button>
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            component={Link}
            to='/customers/create'
          >
            {translate('dashboard.new_customer')}
          </Button>
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            component={Link}
            to='/appointments/create'
          >
            {translate('dashboard.new_appointment')}
          </Button>
          <Button variant='outlined' startIcon={<PhotoIcon />} component={Link} to='/photos'>
            {translate('dashboard.photos')}
          </Button>
        </Stack>
      </Stack>

      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {data && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Inventory boats={data.boats} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Upcoming appointments={data.appointments} users={data.users} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Todo boats={data.boats} />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Portals listings={data.listings} account={data.account} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Recent changes={data.recent} boats={data.boats} users={data.users} />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default Dashboard
